/**
 * «Чаты · Аналитика» (`/chat-stats`, `GET /api/chats/stats`): обращения клиентов, скорость
 * ответа и что было в CRM на момент обращения. Задача владельца 10.10.26: по входящим звонкам
 * процент отвеченных есть, по чатам — нет. Спека `docs/specs/2026-10-10-chat-stats-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { apiFetch } from "@/lib/api";
import { iso, plusDays } from "@/lib/dates";

type Buckets = Record<string, number>;
type Numbers = {
  contacts: number;
  answered: number;
  answered_pct: number;
  chats: number;
  median_sec: number | null;
  mean_sec: number | null;
  fast_pct: number;
  buckets: Buckets;
  zapis: number;
  booked: number;
};
type Total = Numbers & { answers_with_user: number; answers_without_user: number };
type Row = Numbers & Record<string, unknown>;
type Data = {
  from: string;
  to: string;
  hours: [number, number];
  window_hours: number;
  buckets: { key: string; label: string }[];
  states: { key: string; label: string }[];
  total: Total;
  channels: (Row & { channel: string })[];
  accounts: (Row & { key: string; channel: string; place: string; name: string })[];
  staff: (Row & { user: number | null; name: string })[];
  state_rows: (Row & { key: string; label: string })[];
  days: (Row & { day: string })[];
};

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const КАНАЛ: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  max: "MAX",
  avito: "Авито",
  vk: "ВКонтакте",
  instagram: "Instagram",
};
const МЕСТО: Record<string, string> = { app: "в кабинете", web: "в браузере" };

/** 778 → «13 мин», 95 → «1,6 мин», 42 → «42 с». */
function время(sec: number | null): string {
  if (sec === null) return "—";
  if (sec < 60) return `${sec} с`;
  const мин = sec / 60;
  if (мин < 10) return `${мин.toFixed(1).replace(".", ",")} мин`;
  if (мин < 180) return `${Math.round(мин)} мин`;
  return `${(мин / 60).toFixed(1).replace(".", ",")} ч`;
}
const день = (i: string) => i.slice(8, 10) + "." + i.slice(5, 7);

/** Крупная плитка итога. */
function Tile({ label, value, hint, tone = "" }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className={`${card} min-w-[132px] flex-1 px-3.5 py-2.5`} data-annot={`плитка «${label}»`}>
      <div className="text-[11.5px] text-fg-muted">{label}</div>
      <div className={`text-[22px] font-semibold leading-tight tracking-[-0.02em] tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11px] text-fg-faint">{hint}</div>}
    </div>
  );
}

const th = "whitespace-nowrap border-b border-border px-2.5 py-1.5 text-left text-[11.5px] font-medium text-fg-muted";
const td = "whitespace-nowrap px-2.5 py-1.5 tabular-nums";

/** Таблица разреза: подпись группы + одинаковые числа. */
function Cut({
  title,
  hint,
  head,
  rows,
  label,
  d,
}: {
  title: string;
  hint?: string;
  head: string;
  rows: Row[];
  label: (r: Row) => React.ReactNode;
  d: Data;
}) {
  if (!rows.length) return null;
  return (
    <section className={card} data-annot={`разрез «${title}»`}>
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-2.5">
        <h2 className="text-[13px] font-medium">{title}</h2>
        {hint && <span className="text-[11.5px] text-fg-faint">{hint}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr>
              <th className={`${th} pl-4`}>{head}</th>
              <th className={th}>Обращений</th>
              <th className={th}>Ответили</th>
              <th className={th}>≤ 2 мин</th>
              <th className={th}>Медиана</th>
              <th className={th}>Среднее</th>
              {d.buckets.map((b) => (
                <th key={b.key} className={th}>
                  {b.label}
                </th>
              ))}
              <th className={th}>→ запись</th>
              <th className={th}>→ бронь</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border last:border-b-0 hover:bg-bg-hover">
                <td className="px-2.5 py-1.5 pl-4">{label(r)}</td>
                <td className={`${td} font-semibold`}>{r.contacts}</td>
                <td className={td}>
                  {r.answered}
                  <span className={`ml-1.5 text-[11.5px] ${r.answered_pct < 80 ? "text-err" : "text-fg-faint"}`}>
                    {r.answered_pct}%
                  </span>
                </td>
                <td className={`${td} ${r.fast_pct >= 50 ? "text-ok" : r.fast_pct < 20 ? "text-warn" : ""}`}>
                  {r.fast_pct}%
                </td>
                <td className={td}>{время(r.median_sec)}</td>
                <td className={`${td} text-fg-muted`}>{время(r.mean_sec)}</td>
                {d.buckets.map((b) => (
                  <td key={b.key} className={`${td} ${b.key === "none" ? "text-err" : "text-fg-muted"}`}>
                    {r.buckets[b.key] || <span className="text-fg-faint">·</span>}
                  </td>
                ))}
                <td className={td}>{r.zapis || <span className="text-fg-faint">·</span>}</td>
                <td className={`${td} font-medium`}>{r.booked || <span className="text-fg-faint">·</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function ChatStatsPage() {
  const [params, setParams] = useSearchParams();
  const to = params.get("to") || iso(new Date());
  const from = params.get("from") || iso(plusDays(new Date(to), -6));
  const set = (p: Record<string, string | undefined>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(p)) v === undefined ? out.delete(k) : out.set(k, v);
    setParams(out, { replace: true });
  };
  const q = useQuery({
    queryKey: ["chat-stats", from, to],
    queryFn: () => apiFetch<Data>(`/api/chats/stats?from=${from}&to=${to}`),
  });
  const d = q.data;
  const t = d?.total;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-annot="шапка аналитики чатов">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Чаты · Аналитика</h1>
        <DateRangePicker
          label="Период"
          from={from}
          to={to}
          onChange={(a, b) => set({ from: a, to: b })}
        />
        {d && (
          <span className="text-[11.5px] text-fg-faint">
            обращение — сообщение клиента после {d.window_hours} ч тишины · время ответа считаем в рабочие часы{" "}
            {d.hours[0]}:00–{d.hours[1]}:00
          </span>
        )}
      </div>
      {q.isPending && <p className="text-fg-muted">Загружаю…</p>}
      {q.error && <p className="text-err">{String(q.error)}</p>}
      {d && t && (
        <>
          <div className="flex flex-wrap gap-2" data-annot="итог аналитики чатов">
            <Tile label="Обращений" value={String(t.contacts)} hint={`в ${t.chats} диалогах`} />
            <Tile label="Ответили" value={`${t.answered_pct}%`} hint={`${t.answered} из ${t.contacts}`} />
            <Tile
              label="Ответили ≤ 2 мин"
              value={`${t.fast_pct}%`}
              hint="мессенджер — мгновенные сообщения"
              tone={t.fast_pct < 30 ? "text-warn" : "text-ok"}
            />
            <Tile label="Медиана ответа" value={время(t.median_sec)} hint={`среднее ${время(t.mean_sec)}`} />
            <Tile label="Без ответа" value={String(t.buckets.none ?? 0)} tone="text-err" />
            <Tile label="→ запись" value={String(t.zapis)} hint={`бронь ${t.booked}`} />
          </div>
          <p className={`${card} px-4 py-2.5 text-[12.5px] text-fg-muted`} data-annot="атрибуция ответов">
            Автор ответа известен у <b className="text-fg">{t.answers_with_user}</b> ответов из{" "}
            {t.answers_with_user + t.answers_without_user}: это ответы из кабинета.{" "}
            <b className="text-fg">{t.answers_without_user}</b> отправлены из браузера сотрудника — там автора не
            видно, такие ответы в разрезе по сотрудникам собраны одной строкой. Чем больше ответов идёт из «Чатов»,
            тем полнее этот разрез.
          </p>
          <Cut
            d={d}
            title="По каналам"
            head="Канал"
            rows={d.channels}
            label={(r) => КАНАЛ[r.channel as string] ?? (r.channel as string)}
          />
          <Cut
            d={d}
            title="По состоянию CRM на момент обращения"
            hint="что было активно в ту минуту, когда клиент написал, — а не сейчас"
            head="Состояние"
            rows={d.state_rows}
            label={(r) => r.label as string}
          />
          <Cut
            d={d}
            title="По аккаунтам"
            hint="куда писали: официальный вход канала или наш номер в браузере сотрудника"
            head="Аккаунт"
            rows={d.accounts}
            label={(r) => (
              <span className="flex items-baseline gap-1.5">
                <span>{КАНАЛ[r.channel as string] ?? (r.channel as string)}</span>
                <span className="text-fg-muted">{r.name as string}</span>
                <span className="text-[11px] text-fg-faint">{МЕСТО[r.place as string]}</span>
              </span>
            )}
          />
          <Cut
            d={d}
            title="По сотрудникам"
            hint="кто ответил первым на обращение"
            head="Сотрудник"
            rows={d.staff}
            label={(r) => (
              <span className={r.user ? "" : "text-fg-muted italic"}>{r.name as string}</span>
            )}
          />
          <Cut d={d} title="По дням" head="День" rows={d.days} label={(r) => день(r.day as string)} />
        </>
      )}
    </main>
  );
}
